import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVendor } from '../contexts/VendorContext';
import LanguageSwitcher from './LanguageSwitcher';
import bookMarketLogo from '../assets/book-market-logo.png';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, user, vendor, logout, pendingOrders, isPendingVendor } = useVendor();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10 lg:ml-64">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-14 sm:h-16">
          <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md text-gray-600 active:text-gray-900 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 min-w-[44px] min-h-[44px] touch-manipulation"
              aria-label="Open sidebar"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="ml-2 sm:ml-4 lg:ml-0 flex items-center">
              <img src={bookMarketLogo} alt="BookMarketLk" className="h-9 sm:h-10 w-auto" />
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated && !isPendingVendor && (
              <Link
                to="/orders"
                className="relative p-2 rounded-md text-gray-600 active:text-gray-900 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                aria-label={t('nav.orders')}
                title={t('nav.orders')}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {pendingOrders > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center">
                    {pendingOrders}
                  </span>
                )}
              </Link>
            )}

            <LanguageSwitcher className="hidden sm:block" />

            {isAuthenticated ? (
              <>
                {user && (
                  <Link
                    to="/profile"
                    className="hidden sm:flex items-center gap-2 text-xs sm:text-sm text-gray-500 active:text-gray-700 min-h-[44px] touch-manipulation"
                  >
                    {vendor?.profile_picture ? (
                      <img
                        src={vendor.profile_picture}
                        alt={vendor.name}
                        className="w-7 h-7 rounded-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-blue-600">
                          {(user.first_name?.[0] || user.username[0] || '').toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="max-w-[160px] truncate">{vendor?.business_name || vendor?.name || user.username}</span>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="text-gray-600 active:text-gray-900 px-2 sm:px-3 py-2 rounded-md text-sm font-medium transition-colors min-h-[44px] touch-manipulation"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="text-gray-600 active:text-gray-900 px-2 sm:px-3 py-2 rounded-md text-sm font-medium transition-colors min-h-[44px] flex items-center touch-manipulation"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
